import React, { useState, useCallback, useEffect } from 'react';
import { Box, Alert, Skeleton, Button } from '@mui/material';
import { IconExternalLink, IconRefresh } from '@tabler/icons-react';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarCheckbox,
  ToolbarSpacer,
  ToolbarDivider,
  ToolbarCount,
  ToolbarButton,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
  DataTablePagination,
} from '../components/table';
import { Chip } from '../components/chip';
import type {
  ProductsListItem,
  ProductsSortField,
} from '@techgitter/contracts';
import { fetchInventory } from '../services/inventoryApi';
import { refreshProductsCache } from '../services/productCacheService';

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
});

const formatCurrency = (n: number | null | undefined): string =>
  currencyFormatter.format(Number(n) || 0);

type SortableColumn = ProductsSortField;

const columnLabels: Record<SortableColumn, string> = {
  sku: 'SKU',
  displayName: 'Name',
  category: 'Category',
  unitCost: 'Unit Cost',
};

interface InventoryPageProps {
  userInfo?: { displayName: string; userPrincipalName: string; roles: string[] } | null;
}

export const InventoryPage: React.FC<InventoryPageProps> = ({ userInfo }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [sort, setSort] = useState<ProductsSortField>('sku');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [items, setItems] = useState<ProductsListItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const isAdmin = userInfo?.roles.includes('admin') ?? false;

  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<
    { severity: 'success' | 'error'; text: string } | null
  >(null);

  const handleRefreshCache = useCallback(async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await refreshProductsCache();
      if (res.success) {
        setSyncMessage({
          severity: 'success',
          text: `Products cache refreshed — ${res.items?.length ?? 0} products loaded.`,
        });
        // Refresh the table to reflect the newly cached data.
        setRetryNonce((n) => n + 1);
      } else {
        setSyncMessage({
          severity: 'error',
          text: res.error || 'Products cache refresh failed',
        });
      }
    } catch (err) {
      setSyncMessage({
        severity: 'error',
        text: err instanceof Error ? err.message : 'Products cache refresh failed',
      });
    } finally {
      setSyncing(false);
    }
  }, []);

  // Debounce search input — 300ms matches other search pages.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset to page 0 on filter/sort change.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, includeInactive, sort, order]);

  // Main fetch effect.
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    fetchInventory({
      page: page + 1,
      pageSize: rowsPerPage,
      sort,
      order,
      search: debouncedSearch || undefined,
      includeInactive: includeInactive || undefined,
      signal: ctrl.signal,
    })
      .then((res) => {
        setItems(res.items);
        setTotalItems(res.pagination.totalItems);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [debouncedSearch, includeInactive, page, rowsPerPage, sort, order, retryNonce]);

  const handleSortClick = useCallback(
    (field: SortableColumn) => {
      if (sort === field) {
        setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSort(field);
        setOrder('asc');
      }
    },
    [sort],
  );

  const handleRetry = () => setRetryNonce((n) => n + 1);

  const showingStart = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const showingEnd = Math.min(totalItems, (page + 1) * rowsPerPage);

  return (
    <>
      {/* Contextual toolbar — floating card on the grey page surface
          (main-content--inventory), matching the Invoices page. */}
      <SecondaryToolbar floating>
        <ToolbarSearch
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search by SKU or name…"
        />
        {isAdmin && (
          <ToolbarCheckbox
            label="Include inactive"
            active={includeInactive}
            onClick={() => setIncludeInactive((v) => !v)}
          />
        )}
        <ToolbarSpacer />
        <ToolbarCount>
          {totalItems === 0
            ? 'No results'
            : `Showing ${showingStart}–${showingEnd} of ${totalItems}`}
        </ToolbarCount>
        {isAdmin && (
          <>
            <ToolbarDivider />
            <ToolbarButton
              variant="secondary"
              icon={IconExternalLink}
              title="Sync product & pricing data from Xero into RepairShopr (opens RepairShopr in a new tab)"
              onClick={() =>
                window.open(
                  'https://techsolvers.repairshopr.com/xero_settings/sync_xero_products?products=true',
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              Xero Sync
            </ToolbarButton>
            <ToolbarButton
              variant="secondary"
              icon={IconRefresh}
              disabled={syncing}
              title="Refresh the products cache — clears the cached product data and reloads it fresh"
              onClick={handleRefreshCache}
            >
              {syncing ? 'Refreshing…' : 'Refresh Products'}
            </ToolbarButton>
          </>
        )}
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', maxWidth: 1600, mx: 'auto' }}>
        {/* Sync feedback banner */}
      {syncMessage && (
        <Alert
          severity={syncMessage.severity}
          sx={{ mb: 2 }}
          onClose={() => setSyncMessage(null)}
        >
          {syncMessage.text}
        </Alert>
      )}

        {/* Error banner */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Table */}
      <DataTableCard>
        <DataTable>
          <thead>
            <tr>
              <DataTableHeadCell sortable active={sort === 'sku'} direction={order} onSort={() => handleSortClick('sku')}>
                {columnLabels.sku}
              </DataTableHeadCell>
              <DataTableHeadCell sortable active={sort === 'displayName'} direction={order} onSort={() => handleSortClick('displayName')}>
                {columnLabels.displayName}
              </DataTableHeadCell>
              <DataTableHeadCell sortable active={sort === 'category'} direction={order} onSort={() => handleSortClick('category')}>
                {columnLabels.category}
              </DataTableHeadCell>
              <DataTableHeadCell align="right" sortable active={sort === 'unitCost'} direction={order} onSort={() => handleSortClick('unitCost')}>
                {columnLabels.unitCost}
              </DataTableHeadCell>
              <DataTableHeadCell>Vendor Product</DataTableHeadCell>
              {includeInactive && <DataTableHeadCell>Status</DataTableHeadCell>}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 8 }).map((_, i) => (
                <DataTableRow key={`skeleton-${i}`}>
                  {Array.from({ length: includeInactive ? 6 : 5 }).map((__, j) => (
                    <DataTableCell key={j}>
                      <Skeleton variant="text" />
                    </DataTableCell>
                  ))}
                </DataTableRow>
              ))}
            {!loading && items.length === 0 && !error && (
              <DataTableEmpty colSpan={includeInactive ? 6 : 5}>
                No products found
              </DataTableEmpty>
            )}
            {!loading &&
              items.map((product) => (
                <DataTableRow key={product.id}>
                  <DataTableCell bold>{product.sku}</DataTableCell>
                  <DataTableCell>{product.displayName || '—'}</DataTableCell>
                  <DataTableCell>
                    {product.category ? <Chip>{product.category}</Chip> : '—'}
                  </DataTableCell>
                  <DataTableCell align="right">
                    {formatCurrency(product.unitCost)}
                  </DataTableCell>
                  <DataTableCell>{product.vendorProductName || '—'}</DataTableCell>
                  {includeInactive && (
                    <DataTableCell>
                      <Chip tone={product.disabled ? 'neutral' : 'success'}>
                        {product.disabled ? 'Inactive' : 'Active'}
                      </Chip>
                    </DataTableCell>
                  )}
                </DataTableRow>
              ))}
          </tbody>
        </DataTable>
        <DataTablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          count={totalItems}
          rowsPerPageOptions={[25, 50, 100, 200]}
          onPageChange={setPage}
          onRowsPerPageChange={(rpp) => {
            setRowsPerPage(rpp);
            setPage(0);
          }}
        />
      </DataTableCard>
      </Box>
    </>
  );
};
